<template>
  <transition name="fade" appear>
    <div class="project-sidesheet">
      <button type="button" class="btn-close project-sidesheet-close" aria-label="Close" @click="close"></button>

      <div class="user-story-detail d-flex flex-column h-100" v-if="localStory">
        <div class="top">
          <div class="row user-story-header">
            <div class="col">
              <h1 class="text-truncate">US-{{ localStory.id }}</h1>
            </div>
            <div class="col-auto">
              <span class="status" v-bind:class="statusClass">{{ storyStatus }}</span>
            </div>
          </div>

          <div class="user-story-story">
            <textarea
              style="width: 100%"
              ref="title"
              class="story-detail-title-textarea form-control"
              v-auto-height
              v-if="edit.title"
              @blur="onFieldBlur('title')"
              v-model="localStory.title"
              @keyup="markDirty"
              placeholder="Title"
            ></textarea>
            <div v-else @click="onFieldClick('title')" v-html="getFormattedField('title')"></div>
          </div>

          <div class="row">
            <div class="col-auto">
              <ul class="timers">
                <li v-if="!editEstimate" @click="editEstimate = true">
                  <span>E</span>
                  {{ localStory.quoted_estimate | toHoursMinutes }}
                </li>
                <li v-else>
                  <span>E</span>
                  <input
                    type="text"
                    class="form-control inline"
                    style="width: 80px; margin-left: 5px"
                    v-model="localStory.quoted_estimate"
                    @keyup="markDirty"
                    @blur="stopEditingEstimate"
                  />
                </li>
                <li>
                  <span>S</span>
                  {{ localStory.time_entries_total | toHoursMinutes }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="bottom" style="flex-grow: 1; overflow: hidden scroll">
          <div class="user-story-meta">
            <div class="row mb-2">
              <label class="control-label col-4">Tags:</label>
              <div class="col-8">
                <tag :value="localStory.tags" @input="handleSelectTagCallback" @tag="handleAddNewTag"></tag>
              </div>
            </div>
            <div class="row mb-2">
              <label class="control-label col-4">Assigned:</label>
              <div class="col-8">
                <user-select
                  defaultClass="multiselectprofile"
                  :user="localStory.user"
                  :userChanged="userChanged"
                  :inline="true"
                ></user-select>
              </div>
            </div>
          </div>
          <div class="user-story-description">
            <h2 class="strong mt-4">Description</h2>
            <wysiwyg-inline
              v-if="focused"
              ref="description"
              style="width: 100%"
              class="story-detail-textarea form-control"
              v-auto-height
              :content="localStory.description"
              :save="saveDescription"
              :blur="blur"
              :focus="focusWysiwyg"
              placeholder="Description"
            ></wysiwyg-inline>
            <div
              v-else
              @click="wysiwygFocus"
              v-html="localStory.description"
              style="min-height: 40px; min-width: 50px"
            ></div>
          </div>

          <div class="user-story-tech-notes">
            <h2 class="strong mt-4">Technical Notes</h2>
            <textarea
              style="width: 100%"
              ref="tech_notes"
              class="story-detail-textarea form-control"
              v-auto-height
              v-if="edit.tech_notes"
              @blur="onFieldBlur('tech_notes')"
              v-model="localStory.tech_notes"
              @keyup="markDirty"
              placeholder="tech_notes"
            ></textarea>
            <div
              v-else
              @click="onFieldClick('tech_notes')"
              v-html="getFormattedField('tech_notes')"
              style="min-height: 40px; min-width: 50px"
            ></div>
          </div>

          <audio
            v-if="localStory.recording"
            id="audio"
            preload="auto"
            controls="true"
            v-bind:src="localStory.recording"
          ></audio>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { filter, cloneDeep } from 'lodash'
import addItem from './_add-item'
import { mapState, mapActions, mapGetters } from 'vuex'
import userstoryCommon from 'mixins/scrumboard/userstory-common'
import { WysiwygInline } from "components/form/input"

export default {
  mixins: [userstoryCommon],
  components: {
    addItem,
    WysiwygInline
  },

  data: function () {
    return {
      focused: false,
      focusWysiwyg: false,
      focusStoryId: null,
      loaded: false,
      localStory: null,
      editEstimate: false,
    }
  },

  //Handle fresh page loads to this uri, need to load stories
  // beforeRouteUpdate (to, from, next) {

  //     this.getUserStory(to.params.story_id).then(() => {
  //        this.loaded = true
  //     });

  //     next()
  // },

  watch: {
    //We need to watch the state object to react to any broadcasted updates
    story: function (newVal, oldVal) {
      this.localStory = cloneDeep(this.story)
    },
  },

  mounted() {
    this.localStory = cloneDeep(this.story)
  },

  computed: {
    ...mapState('yodizproject', {
      project: 'single',
    }),

    ...mapState('yodizproject/tasks', {
      tasks: 'list',
    }),

    ...mapGetters('yodizproject/stories', {
      getById: 'getById',
    }),

    ...mapGetters('yodizproject/backlog', {
      getBacklogStoryById: 'getById',
    }),

    story() {
      var story = this.getById(this.$route.params.story_id)

      if (!story) {
        story = this.getBacklogStoryById(this.$route.params.story_id)
      }

      return story
    },

    ...mapGetters('yodizproject/tasks', {
      getByStoryIdAndStatus: 'getByStoryIdAndStatus',
      getCountOfStatus: 'getCountOfStatus',
    }),

    ...mapGetters('yodizproject/stories', {
      getById: 'getById',
    }),

    parentRouteName() {
      return this.$route.matched[this.$route.matched.length - 2].name
    },
  },

  methods: {
    ...mapActions('yodizproject/stories', {
      getStories: 'getList',
      getUserStory: 'getSingle',
    }),

    ...mapActions('yodizproject/tasks', {
      updateTask: 'updateSingle',
    }),

    blur () {
      this.focused = false
      this.focusWysiwyg = false
    },

    saveDescription(value) {
      return new Promise(() => {
        this.localStory.description = value
        this.dirty = true
        this.focusWysiwyg = false
        this.focused = false
        this.handleUpdateUserStory()
      })
    },

    wysiwygFocus() {
      this.focusWysiwyg = true
      this.focused = true
    },

    close() {
      //either need to go back in history or to the parent route
      this.$router.push({ name: this.parentRouteName, params: this.$route.params })
    },

    stopEditingEstimate() {
      this.editEstimate = false
      this.handleUpdateUserStory()
    },
  },
}
</script>

<style scoped>
.story-detail-title-textarea {
  height: 201px;
  overflow: hidden;
  padding: 0px;
  border: none;
  width: 100%;
  font-size: 16px;
  font-weight: 500;
}

.story-detail-textarea {
  height: 201px;
  overflow: hidden;
  padding: 0px;
  border: none;
  width: 100%;
  font-size: 16px;
}

ul.timers {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-top: 10px;
}

ul.timers li {
  margin-top: 5px;
  display: inline;
  color: #6d6d6d;
}

ul.timers li:not(:first-child) {
  margin-left: 0.375rem;
}

ul.timers span {
  font-weight: 700;
  color: #424242;
}
</style>
